import { currentViewConfigAtom } from "datasheets/model/atoms/WorkbookFocusAtoms";
import { atom, useAtom, useAtomValue, useSetAtom } from "jotai";
import { ViewConfig } from "datasheets/model/core/Workbook";
import { Box, Button, Stack } from "@mui/material";
import { FormViewConfig, viewType } from "./FormViewConfig";
import { FormComponentFactory } from "./FormComponentFactory";
import { Component } from "./Component";
import { PropsWithChildren } from "react";
import { useDrop } from "react-dnd";
import { PropertiesEditor } from "./PropertiesEditor";
import { FixedComponent } from "./FixedComponent";
import { EditableComponent } from "./EditableComponent";
import { ComponentPalette } from "./ComponentPalette";
import { ViewProps, ViewFactory } from "datasheets/model/ViewFactory";
import { ViewReplicator } from "datasheets/model/logic/replicate";
import { PersistentKeyMapper } from "datasheets/model/utils/dictionaryUtils";

const editModeAtom = atom(
  (get) => (get(currentViewConfigAtom) as FormViewConfig).edit,
  (get, set, arg) => {
    const prevView = get(currentViewConfigAtom) as FormViewConfig;
    const prevMode = prevView.edit;
    const nextMode = typeof arg === "function" ? arg(prevMode) : arg;
    set(currentViewConfigAtom, { ...prevView, ...{ edit: nextMode } });
  }
);

function persistAllEditConfig(components: { [key: string]: Component }): {
  [key: string]: Component;
} {
  const newComponents = Object.entries(components).reduce<{
    [key: string]: Component;
  }>((prev, [key, config]) => {
    prev[key] = { ...config, ...{ config: config.editConfig } };
    return prev;
  }, {});
  return newComponents;
}

export function Form({ className }: { className?: string }) {
  const editMode = useAtomValue(editModeAtom);
  const viewComponent = !!editMode ? <FormConfiguration /> : <FormDisplay />;
  return (
    <div className={className} style={{ textAlign: "left" }}>
      {viewComponent}
    </div>
  );
}

function FormDisplay() {
  const setEditMode = useSetAtom(editModeAtom);
  function enterEditMode() {
    setEditMode(true);
  }
  return (
    <>
      <Box height="2vh" />
      <Box sx={{ float: "right" }}>
        <Button onClick={enterEditMode}>Edit</Button>
      </Box>
      <FormLayout />
    </>
  );
}

function FormConfiguration() {
  const setViewConfig = useSetAtom(currentViewConfigAtom);
  const addConfig = (componentConfig: Component) => {
    setViewConfig((viewConfig: ViewConfig) => {
      const formConfig = viewConfig as FormViewConfig;
      const newConfig: FormViewConfig = {
        ...formConfig,
        ...{
          components: {
            ...formConfig.components,
            ...{ [componentConfig.id]: componentConfig },
          },
        },
      };
      return newConfig;
    });
  };
  const saveLayout = () => {
    setViewConfig((config: ViewConfig) => {
      const formConfig = config as FormViewConfig;
      const newFormConfig: FormViewConfig = {
        ...formConfig,
        ...{
          edit: false,
          components: persistAllEditConfig(formConfig.components),
        },
      };
      return newFormConfig;
    });
  };
  return (
    <>
      <Stack direction="row">
        <div style={{ justifyContent: "center", width: "80vw" }}>
          <ComponentPalette addConfig={addConfig} />
        </div>
        <Button onClick={saveLayout}>Save</Button>
      </Stack>
      <FormLayout>
        <PropertiesEditor />
      </FormLayout>
    </>
  );
}

function FormLayout({ children }: PropsWithChildren<{}>) {
  const [viewConfig, setViewConfig] = useAtom(currentViewConfigAtom);
  const formConfig = viewConfig as FormViewConfig;
  const [, drop] = useDrop({
    accept: "COMPONENT_DRAG",
    drop({ id }: { id: string }, monitor): void {
      const delta = monitor.getDifferenceFromInitialOffset();
      fixPosition(id, delta?.x ?? 0, delta?.y ?? 0);
    },
    collect: (monitor: any) => ({
      canDrop: monitor.canDrop(),
    }),
  });
  function fixPosition(componentId: string, left: number, top: number) {
    const componentConfig = formConfig.components[componentId];
    const newComponentConfig = {
      ...componentConfig,
      ...{ top: componentConfig.top + top, left: componentConfig.left + left },
    };
    setViewConfig((view: FormViewConfig) => {
      return {
        ...view,
        ...{
          components: {
            ...view.components,
            ...{ [componentId]: newComponentConfig },
          },
        },
      };
    });
  }
  const components = Object.values(formConfig.components).map((config) =>
    !!formConfig.edit ? (
      <EditableComponent config={config} key={config.id}>
        {FormComponentFactory.generateComponent(config.type, config)}
      </EditableComponent>
    ) : (
      <FixedComponent config={config} key={config.id}>
        {FormComponentFactory.generateComponent(config.type, config)}
      </FixedComponent>
    )
  );
  return (
    <div
      style={{
        // backgroundColor: "grey",
        backgroundColor: "#dddddd",
        width: "100vw",
        height: "80vh",
      }}
      ref={drop}
    >
      {children}
      {components}
    </div>
  );
}

function generate(): FormViewConfig {
  return {
    type: viewType,
    edit: true,
    components: {},
  };
}
function component(props: ViewProps) {
  return <Form className={props.className} />;
}
export default {
  register: () => {
    ViewFactory.addView(viewType, generate, component);
    ViewReplicator.addReplicator(
      viewType,
      (
        config: ViewConfig,
        propertyRemap: PersistentKeyMapper
      ): FormViewConfig => {
        return { ...(config as FormViewConfig) };
      }
    );
  },
};
